import { Link } from 'react-router-dom';
import { ArrowUpRight, ArrowRight } from 'lucide-react';
import { Eyebrow, SectionHeader, TextLink } from '@/components/Atoms';
import { WhyClientsBlock, FunnelBlock, BusinessOutcomesBlock } from '@/components/Blocks';
import { HOME } from '@/constants/testIds';
import { SERVICES } from '@/data/services';
import { PORTFOLIO } from '@/data/portfolio';
import WallOfTrust from '@/components/WallOfTrust';
import { HALLIE } from '@/data/images';
import { PERSON_SCHEMA } from '@/data/schema';
import useSEO from '@/hooks/useSEO';

const WHAT_I_HELP = [
  { title: 'Operations Management', body: 'Creating structure, accountability, and systems that improve efficiency and reduce day-to-day friction.' },
  { title: 'Customer Experience', body: 'Building processes that improve responsiveness, consistency, and customer satisfaction.' },
  { title: 'Recruitment & Team Support', body: 'Helping businesses attract, onboard, and support reliable team members.' },
  { title: 'CRM & Systems Management', body: 'Organizing customer data, workflows, and automations to improve visibility and follow-through.' },
  { title: 'Website Development', body: 'Building conversion-focused websites designed to support business growth.' },
  { title: 'SEO Optimization', body: 'Helping local businesses improve online visibility and attract qualified leads.' },
  { title: 'Social Media Management', body: 'Creating consistent, professional content that builds trust and strengthens brand presence.' },
];

export default function Home() {
  useSEO({
    title: 'Operations & Systems Specialist for Service Businesses',
    description:
      'Hallie · Eureka1 helps service-based businesses streamline operations, improve customer experiences, and build reliable systems that support sustainable growth.',
    path: '/',
    schema: PERSON_SCHEMA,
  });

  return (
    <div data-testid={HOME.hero}>
      {/* HERO */}
      <section className="relative pt-12 lg:pt-24 pb-24 lg:pb-40 overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
            <div className="lg:col-span-7 reveal">
              <Eyebrow>Reliable Support, Handled with Care.</Eyebrow>
              <h1 className="font-serif-display text-[44px] sm:text-6xl lg:text-[96px] leading-[0.98] tracking-tight mt-6 text-[#1C1C1C]">Hallie.</h1>
              <p className="font-serif-italic italic text-2xl sm:text-3xl text-[#C9A24D] mt-4 leading-snug">Operations & Systems Specialist.</p>
              <p className="mt-8 text-lg lg:text-xl text-[#1C1C1C]/75 max-w-xl leading-relaxed">
                Helping service-based businesses streamline operations, improve customer experiences, and build reliable systems that support sustainable growth.
              </p>
              <div className="mt-12 flex flex-col sm:flex-row gap-4">
                <Link to="/contact" data-testid={HOME.heroCta} className="btn-primary">Book a Discovery Call</Link>
                <Link to="/portfolio" data-testid={HOME.heroSecondary} className="btn-ghost">View Portfolio</Link>
              </div>
              <div className="mt-16 flex items-center gap-8">
                <div>
                  <div className="num-marker">15+</div>
                  <p className="text-xs uppercase tracking-[0.18em] text-[#1C1C1C]/60 mt-2">Years of<br/>experience</p>
                </div>
                <div className="hairline-gold" />
                <div>
                  <div className="num-marker">7</div>
                  <p className="text-xs uppercase tracking-[0.18em] text-[#1C1C1C]/60 mt-2">Disciplines<br/>under one roof</p>
                </div>
              </div>
            </div>

            <div className="lg:col-span-5 relative reveal reveal-delay-2">
              <div className="relative aspect-[4/5] overflow-hidden">
                <img src={HALLIE.heroLight} alt="Hallie — Operations & Systems Specialist" className="w-full h-full object-cover editorial-img" />
              </div>
              <div className="absolute -bottom-8 -left-8 hidden lg:block bg-[#F4EFEA] p-7 max-w-xs border border-[#E6CFC7]">
                <p className="font-serif-italic italic text-xl text-[#1C1C1C] leading-snug">
                  &ldquo;Reduce complexity, improve consistency, and create systems business owners can rely on.&rdquo;
                </p>
                <p className="text-xs tracking-[0.18em] uppercase text-[#C9A24D] mt-5">Hallie</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHO I HELP */}
      <section data-testid={HOME.whoIHelp} className="border-t border-[#E6CFC7]/70 py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5">
            <Eyebrow>Who I Help</Eyebrow>
            <h2 className="font-serif-display text-4xl sm:text-5xl mt-5 leading-[1.05] tracking-tight">
              Service-based businesses<br/>
              <span className="font-serif-italic italic text-[#C9A24D]">that need dependable support.</span>
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pt-4 space-y-6 text-lg text-[#1C1C1C]/80 leading-relaxed max-w-xl">
            <p>I work with service-based businesses that need dependable support, better systems, and stronger customer experiences.</p>
            <p>Whether you are managing a growing cleaning company, home service business, property management firm, or local service brand, I help create the structure and support needed to operate more efficiently and grow with confidence.</p>
          </div>
        </div>
      </section>

      {/* WHAT I HELP WITH */}
      <section data-testid={HOME.whatIHelpWith} className="bg-[#1C1C1C] text-[#F4EFEA] py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="max-w-2xl">
            <Eyebrow>What I Help With</Eyebrow>
            <h2 className="font-serif-display text-4xl sm:text-5xl mt-5 leading-[1.05] tracking-tight">
              Seven disciplines.<br/>
              <span className="font-serif-italic italic text-[#C9A24D]">One reliable partner.</span>
            </h2>
          </div>
          <ol className="mt-16 border-t border-[#F4EFEA]/15">
            {WHAT_I_HELP.map((p, i) => (
              <li key={p.title} className="grid grid-cols-12 gap-6 py-8 lg:py-10 border-b border-[#F4EFEA]/15 items-baseline group">
                <div className="col-span-12 sm:col-span-2 num-marker text-[#C9A24D]">{String(i + 1).padStart(2, '0')}</div>
                <div className="col-span-12 sm:col-span-4">
                  <h3 className="font-serif-display text-2xl sm:text-3xl group-hover:text-[#C9A24D] transition-colors">{p.title}</h3>
                </div>
                <div className="col-span-12 sm:col-span-6 text-[#E6CFC7]/85 leading-relaxed text-base lg:text-lg">{p.body}</div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* WHY HALLIE — narrative */}
      <section data-testid={HOME.whyHallie} className="py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
          <div className="lg:col-span-5 relative">
            <div className="aspect-[3/4] overflow-hidden">
              <img src={HALLIE.editorial2} alt="Hallie editorial portrait" className="w-full h-full object-cover editorial-img" />
            </div>
            <div className="hidden lg:block absolute -top-8 -right-8 bg-[#C9A24D] text-[#1C1C1C] py-3 px-6">
              <span className="font-serif-italic italic">Eureka1</span>
            </div>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <Eyebrow>Why Work With Hallie</Eyebrow>
            <h2 className="font-serif-display text-4xl sm:text-5xl mt-5 leading-[1.05] tracking-tight">
              I don&rsquo;t simply<br/>complete tasks.<br/>
              <span className="font-serif-italic italic text-[#C9A24D]">I help build systems that support growth.</span>
            </h2>
            <div className="mt-8 space-y-5 text-lg text-[#1C1C1C]/80 leading-relaxed">
              <p>My background combines operations management, customer experience, recruitment, sales support, business systems, and process improvement.</p>
              <p>I understand the challenges that come with managing people, customers, schedules, and daily operations because I have spent years helping businesses solve them.</p>
              <p className="font-serif-italic italic text-[#1C1C1C]">
                My goal is simple: reduce complexity, improve consistency, and create systems business owners can rely on.
              </p>
            </div>
            <div className="mt-10">
              <TextLink to="/about" testid="home-about-link">Read the full story</TextLink>
            </div>
          </div>
        </div>
      </section>

      {/* WHY CLIENTS WORK WITH HALLIE — trust grid */}
      <WhyClientsBlock />

      {/* FEATURED SERVICES */}
      <section data-testid={HOME.featuredServices} className="bg-[#FBF7F2] border-y border-[#E6CFC7]/70 py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-10">
            <SectionHeader eyebrow="Featured Services" title="The work I do" italic="for service businesses." kicker="Engagements range from a single project to a continuing operations partnership." />
            <TextLink to="/services" testid="home-all-services-link">All services</TextLink>
          </div>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {SERVICES.map((s) => (
              <Link key={s.slug} to={`/services/${s.slug}`} data-testid={`${HOME.serviceCard}-${s.slug}`} className="card-soft p-8 lg:p-10 group block">
                <div className="flex items-start justify-between gap-6">
                  <div className="eyebrow">{s.eyebrow}</div>
                  <ArrowUpRight size={22} className="text-[#1C1C1C]/40 group-hover:text-[#C9A24D] group-hover:-translate-y-1 group-hover:translate-x-1 transition-all duration-500" />
                </div>
                <h3 className="font-serif-display text-2xl lg:text-3xl mt-6 leading-snug">{s.name}</h3>
                <p className="mt-4 text-[15px] text-[#1C1C1C]/70 leading-relaxed">{s.short}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* BUSINESS OUTCOMES */}
      <BusinessOutcomesBlock />

      {/* FEATURED PORTFOLIO (text-only) */}
      <section data-testid={HOME.featuredResults} className="py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-10">
            <SectionHeader eyebrow="Featured Work" title="Quiet systems." italic="Steady results." />
            <TextLink to="/portfolio" testid="home-portfolio-link">View portfolio</TextLink>
          </div>
          <div className="mt-16 border-t border-[#E6CFC7]/70">
            {PORTFOLIO.slice(0, 3).map((p, idx) => (
              <Link
                key={p.slug}
                to={`/portfolio/${p.slug}`}
                data-testid={`home-portfolio-card-${p.slug}`}
                className="grid grid-cols-1 lg:grid-cols-12 gap-6 py-10 lg:py-12 border-b border-[#E6CFC7]/70 hover:bg-[#FBF7F2] transition-colors group items-baseline"
              >
                <div className="lg:col-span-1 num-marker">{String(idx + 1).padStart(2, '0')}</div>
                <div className="lg:col-span-7">
                  <div className="eyebrow">{p.industry} — {p.year}</div>
                  <h3 className="font-serif-display text-2xl lg:text-3xl mt-3 leading-snug group-hover:text-[#C9A24D] transition-colors">{p.title}</h3>
                  <p className="mt-3 text-sm text-[#1C1C1C]/65 leading-relaxed max-w-xl">{p.summary}</p>
                </div>
                <div className="lg:col-span-3 lg:pt-1">
                  <div className="eyebrow">Outcomes</div>
                  <ul className="mt-2 space-y-1.5 text-sm text-[#1C1C1C]/75">
                    {p.outcomes.slice(0, 2).map((o, i) => (
                      <li key={i} className="flex gap-3"><span className="text-[#C9A24D]">—</span>{o}</li>
                    ))}
                  </ul>
                </div>
                <div className="lg:col-span-1 flex lg:justify-end">
                  <ArrowUpRight size={24} className="text-[#1C1C1C]/40 group-hover:text-[#C9A24D] group-hover:-translate-y-1 group-hover:translate-x-1 transition-all duration-500" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS — Wall of Trust */}
      <WallOfTrust compact />

      {/* DISCOVERY CALL FUNNEL */}
      <FunnelBlock />

      {/* CTA BAND */}
      <section data-testid={HOME.ctaBand}>
        <div className="bg-[#F4EFEA] py-24 lg:py-32 border-t border-[#E6CFC7]/70 text-center">
          <div className="max-w-[1100px] mx-auto px-6 sm:px-10">
            <Eyebrow className="mx-auto">Begin</Eyebrow>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[1.05] tracking-tight">
              Let&rsquo;s build better systems<br/><span className="font-serif-italic italic text-[#C9A24D]">together.</span>
            </h2>
            <p className="mt-7 text-lg text-[#1C1C1C]/75 max-w-2xl mx-auto leading-relaxed">
              A short, focused conversation to learn about your business and how I can help. No pressure, no pitch.
            </p>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <Link to="/contact" data-testid={HOME.ctaBandButton} className="btn-primary">
                Book a Discovery Call <ArrowRight size={16} />
              </Link>
              <Link to="/contact" className="btn-ghost" data-testid="home-cta-band-consult">Request a Consultation</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
